/** Imports ---------------------------------------------------------------- */

/** React imports */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

/** MUI imports */
import { styled } from "@mui/material/styles";
import { Box, Chip, Grid, Typography, Divider, FormControlLabel, Checkbox } from "@mui/material";

/** Component imports */
import { Onboarding, CatchUp, Recurring, Consulting } from "./";

/** Component styles */
const PREFIX = "CFORoot";

const classes = {
    cardContentRoot: `${PREFIX}-cardContentRoot`,
    chip: `${PREFIX}-chip`,
    chipProActive: `${PREFIX}-chipProActive`,
    chipProWasActive: `${PREFIX}-chipProWasActive`,
    selectedChip: `${PREFIX}-selectedChip`,
    selectedChipActive: `${PREFIX}-selectedChipActive`,
    selectedChipWasActive: `${PREFIX}-selectedChipWasActive`,
    divider: `${PREFIX}-divider`,
};

const StyledContainer = styled(Box)(({ theme }) => ({
    [`& .${classes.cardContentRoot}`]: {
        padding: "24px 48px 48px",
    },

    [`& .${classes.chip}`]: {
        padding: 20,
        width: 200,
        backgroundColor: "#EAEAF4",
        color: "#031851",
        fontWeight: "700 !important",
        "&:hover": {
            backgroundColor: "rgba(213, 214, 233, 0.3)",
            color: "#031851",
        },
        borderRadius: 40,
    },

    [`& .${classes.chipProActive}`]: {
        padding: 20,
        width: 200,
        backgroundColor: "#EAEAF4",
        color: "#031851",
        border: "2px solid #ff5968",
        fontWeight: "700 !important",
        "&:hover": {
            backgroundColor: "rgba(213, 214, 233, 0.3)",
            color: "#031851",
        },
        borderRadius: 40,
    },

    [`& .${classes.chipProWasActive}`]: {
        padding: 20,
        width: 200,
        backgroundColor: "#EAEAF4",
        color: "#031851",
        border: "2px solid #031851",
        fontWeight: "700 !important",
        "&:hover": {
            backgroundColor: "rgba(213, 214, 233, 0.3)",
            color: "#031851",
        },
        borderRadius: 40,
    },

    [`& .${classes.selectedChip}`]: {
        padding: 20,
        width: 200,
        backgroundColor: "#2f3190",
        color: "#fff",
        fontWeight: "700 !important",
        borderRadius: 40,
        "&:hover": {
            backgroundColor: "rgba(47, 49, 144, 0.9)",
            color: "#fff",
        },
    },

    [`& .${classes.selectedChipActive}`]: {
        padding: 20,
        width: 200,
        backgroundColor: "#2f3190",
        color: "#fff",
        fontWeight: "700 !important",
        border: "2px solid #ff5968",
        borderRadius: 40,
        "&:hover": {
            backgroundColor: "rgba(47, 49, 144, 0.9)",
            color: "#fff",
        },
    },

    [`& .${classes.selectedChipWasActive}`]: {
        padding: 20,
        width: 200,
        backgroundColor: "#2f3190",
        color: "#fff",
        fontWeight: "700 !important",
        border: "2px solid #031851",
        borderRadius: 40,
        "&:hover": {
            backgroundColor: "rgba(47, 49, 144, 0.9)",
            color: "#fff",
        },
    },

    [`& .${classes.divider}`]: {
        marginBottom: 10,
    },
}));

export const CFORoot = (props) => {
    // Properties
    const {
        cfoSystem,
        clientMarket,
        companyID,
        expenseEst,
        serviceData,
        setalertType,
        setBackDrop,
        setServiceData,
        setShow,
        setTexto,
        setAuxExpanded,
        setExpanded,
        lastChipSelected,
        setLastChipSelected,
    } = props;

    /** Internationalization i18n */
    const [t] = useTranslation("global");

    /* Component states */
    const [valuesAcc, setValuesAcc] = useState({
        cfoOnboard: false,
        cfoCatchUp: false,
        cfoRecu: false,
        cfoConsul: false,
    });
    const [projectsData, setProjectsData] = useState({
        cfoOnboard: {},
        cfoCatchUp: {},
        cfoRecu: {},
        cfoConsul: {},
    });
    const [active, setActive] = useState({
        cfoOnboard: {
            isActive: false,
            wasActive: false,
        },
        cfoCatchUp: {
            isActive: false,
            wasActive: false,
        },
        cfoRecu: {
            isActive: false,
            wasActive: false,
        },
        cfoConsul: {
            isActive: false,
            wasActive: false,
        },
    });
    const [open, setOpen] = useState(false);
    const [infoAdd, setInfoAdd] = useState({});
    const [checkRemind, setCheckRemind] = useState(false);
    const [renderState, setRenderState] = useState(false);

    const handleCloseDialog = () => {
        setInfoAdd({});
        setCheckRemind(false);
        setOpen(false);
    };

    const showConfirm = (value) => {
        setInfoAdd(value);
        setOpen(true);
    };

    const handleChangeCheck = (event) => {
        setCheckRemind(event.target.checked);
    };

    const bodyMess = (
        <>
            <Typography variant="h1" color="primary" align="center" sx={{ fontSize: 18, pt: 3, pb: 2 }}>
                {t("miscellaneous.confRemoveCheckDet")}
            </Typography>
            <FormControlLabel
                control={
                    <Checkbox
                        name="checkedConf"
                        checked={checkRemind}
                        onChange={handleChangeCheck}
                        inputProps={{
                            "aria-label": "controlled",
                        }}
                    />
                }
                label={t("miscellaneous.dontShowAgain")}
            />
        </>
    );

    const getProjects = async (isReload) => {
        setBackDrop(true);

        let serviceCompID = serviceData._id ?? "";

        if (isReload) {
            await fetch(`/getNewServicesCompany?company_id=${companyID}`, {
                method: "GET",
                headers: {
                    "Content-type": "application/json; charset=UTF-8",
                    "Access-Control-Allow-Origin": "*",
                },
            })
                .then((res) => res.json())
                .then(async (data) => {
                    setServiceData(data);
                    let lastProject = data.filter(function (o) {
                        return o.hasOwnProperty("_id") && o.service_name === "CFO";
                    })[0];

                    await fetch(
                        `/getProjectsCompany?company_id=${lastProject.company_id}&service_id=${lastProject.service_id}&service_company_id=${lastProject._id}`,
                        {
                            method: "GET",
                            headers: {
                                "Content-type": "application/json; charset=UTF-8",
                                "Access-Control-Allow-Origin": "*",
                            },
                        }
                    )
                        .then((res) => res.json())
                        .then(async (data) => {
                            let result = {};
                            let lastProyects = {
                                catchUp: {},
                                recurring: {},
                                consulting: {},
                            };
                            let recurringisActive = false;
                            let wasRecurringActive = false;

                            await data.forEach((item) => {
                                switch (item.project_type) {
                                    case 12:
                                        result.cfoOnboard = item;
                                        break;
                                    case 13:
                                        result.cfoRecu = item;
                                        if (item.projects_array.length !== 0) {
                                            let indexExact = 0;
                                            let dateMax = null;
                                            item.projects_array.forEach((item, index) => {
                                                if (index === 0) {
                                                    dateMax = item.end_date;
                                                } else {
                                                    if (moment(dateMax).isBefore(item.end_date)) {
                                                        dateMax = item.end_date;
                                                        indexExact = index;
                                                    }
                                                }
                                                if (item.since_date && !item.client_delivery_date) {
                                                    recurringisActive = true;
                                                } else if (item.client_delivery_date) {
                                                    wasRecurringActive = moment(
                                                        item.client_delivery_date
                                                    ).isSameOrBefore(moment());
                                                }
                                            });
                                            lastProyects.recurring = item.projects_array[indexExact];
                                            result.cfoRecu.project_company_id =
                                                item.projects_array[0].project_company_id;
                                        }
                                        break;
                                    case 14:
                                        result.cfoCatchUp = item;
                                        if (item.projects_array.length !== 0) {
                                            lastProyects.catchUp = item.projects_array[0];
                                            result.cfoCatchUp.project_company_id =
                                                item.projects_array[0].project_company_id;
                                        }
                                        break;
                                    case 15:
                                        result.cfoConsul = item;
                                        if (item.projects_array.length !== 0) {
                                            lastProyects.consulting = item.projects_array[0];
                                            result.cfoConsul.project_company_id =
                                                item.projects_array[0].project_company_id;
                                        }
                                        break;
                                    default:
                                        break;
                                }
                            });

                            setProjectsData({
                                cfoOnboard: result.cfoOnboard,
                                cfoCatchUp: result.cfoCatchUp,
                                cfoRecu: result.cfoRecu,
                                cfoConsul: result.cfoConsul,
                            });

                            setActive({
                                cfoOnboard: {
                                    isActive:
                                        result.cfoOnboard.since_date &&
                                        (!result.cfoOnboard.end_date ||
                                            moment(result.cfoOnboard.end_date).isAfter(moment())),
                                    wasActive: result.cfoOnboard.end_date
                                        ? moment(result.cfoOnboard.end_date).isSameOrBefore(moment())
                                        : false,
                                },
                                cfoCatchUp: {
                                    isActive:
                                        lastProyects.catchUp.since_date && !lastProyects.catchUp.client_delivery_date,
                                    wasActive: lastProyects.catchUp.client_delivery_date
                                        ? moment(lastProyects.catchUp.client_delivery_date).isSameOrBefore(moment())
                                        : false,
                                },
                                cfoRecu: {
                                    isActive: recurringisActive,
                                    wasActive: wasRecurringActive,
                                },
                                cfoConsul: {
                                    isActive: false,
                                    wasActive: lastProyects.consulting.since_date ? true : false,
                                },
                            });

                            setAuxExpanded(lastProject.service_name);
                            setExpanded(true);
                            setRenderState(!renderState);
                        });
                });
        } else {
            await fetch(
                `/getProjectsCompany?company_id=${companyID}&service_id=${serviceData.service_id}&service_company_id=${serviceCompID}`,
                {
                    method: "GET",
                    headers: {
                        "Content-type": "application/json; charset=UTF-8",
                        "Access-Control-Allow-Origin": "*",
                    },
                }
            )
                .then((res) => res.json())
                .then((data) => {
                    let result = {};
                    let lastProyects = {
                        catchUp: {},
                        recurring: {},
                        consulting: {},
                    };
                    let recurringisActive = false;
                    let wasRecurringActive = false;

                    data.forEach((item) => {
                        switch (item.project_type) {
                            case 12:
                                result.cfoOnboard = item;
                                break;
                            case 13:
                                result.cfoRecu = item;
                                if (item.projects_array.length !== 0) {
                                    let indexExact = 0;
                                    let dateMax = null;
                                    item.projects_array.forEach((item, index) => {
                                        if (index === 0) {
                                            dateMax = item.end_date;
                                        } else {
                                            if (moment(dateMax).isBefore(item.end_date)) {
                                                dateMax = item.end_date;
                                                indexExact = index;
                                            }
                                        }
                                        if (!item.client_delivery_date && item.client_commitment_date) {
                                            recurringisActive = true;
                                        } else if (item.client_delivery_date) {
                                            wasRecurringActive = moment(item.client_delivery_date).isSameOrBefore(
                                                moment()
                                            );
                                        }
                                    });
                                    lastProyects.recurring = item.projects_array[indexExact];
                                    result.cfoRecu.project_company_id = item.projects_array[0].project_company_id;
                                }
                                break;
                            case 14:
                                result.cfoCatchUp = item;
                                if (item.projects_array.length !== 0) {
                                    lastProyects.catchUp = item.projects_array[0];
                                    result.cfoCatchUp.project_company_id = item.projects_array[0].project_company_id;
                                }
                                break;
                            case 15:
                                result.cfoConsul = item;
                                if (item.projects_array.length !== 0) {
                                    lastProyects.consulting = item.projects_array[0];
                                    result.cfoConsul.project_company_id = item.projects_array[0].project_company_id;
                                }
                                break;
                            default:
                                break;
                        }
                    });

                    setProjectsData({
                        cfoOnboard: result.cfoOnboard,
                        cfoCatchUp: result.cfoCatchUp,
                        cfoRecu: result.cfoRecu,
                        cfoConsul: result.cfoConsul,
                    });

                    setActive({
                        cfoOnboard: {
                            isActive:
                                result.cfoOnboard.since_date &&
                                (!result.cfoOnboard.end_date || moment(result.cfoOnboard.end_date).isAfter(moment())),
                            wasActive: result.cfoOnboard.end_date
                                ? moment(result.cfoOnboard.end_date).isSameOrBefore(moment())
                                : false,
                        },
                        cfoCatchUp: {
                            isActive: lastProyects.catchUp.since_date && !lastProyects.catchUp.client_delivery_date,
                            wasActive: lastProyects.catchUp.client_delivery_date
                                ? moment(lastProyects.catchUp.client_delivery_date).isSameOrBefore(moment())
                                : false,
                        },
                        cfoRecu: {
                            isActive: recurringisActive,
                            wasActive: wasRecurringActive,
                        },
                        cfoConsul: {
                            isActive: false,
                            wasActive: lastProyects.consulting.since_date ? true : false,
                        },
                    });
                });
        }

        setBackDrop(false);
    };

    /* Component functions */
    useEffect(() => {
        getProjects(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* Component functions */
    useEffect(() => {
        if (lastChipSelected) {
            handleClickChip(valuesAcc[lastChipSelected], lastChipSelected, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [renderState]);

    const handleClickChip = async (value, option, setChip = false) => {
        switch (option) {
            case "cfoOnboard":
                setValuesAcc({
                    ...valuesAcc,
                    cfoOnboard: setChip ? !value : true,
                    cfoCatchUp: false,
                    cfoRecu: false,
                    cfoConsul: false,
                });
                break;
            case "cfoCatchUp":
                setValuesAcc({
                    ...valuesAcc,
                    cfoCatchUp: setChip ? !value : true,
                    cfoOnboard: false,
                    cfoRecu: false,
                    cfoConsul: false,
                });
                break;
            case "cfoRecu":
                setValuesAcc({
                    ...valuesAcc,
                    cfoRecu: setChip ? !value : true,
                    cfoCatchUp: false,
                    cfoOnboard: false,
                    cfoConsul: false,
                });
                break;
            default:
                setValuesAcc({
                    ...valuesAcc,
                    cfoConsul: setChip ? !value : true,
                    cfoRecu: false,
                    cfoCatchUp: false,
                    cfoOnboard: false,
                });
                break;
        }
        if (setChip) {
            setLastChipSelected(option);
        }
    };

    return (
        <StyledContainer>
            <Divider className={classes.divider} />
            <Grid container justifyContent="space-around" alignItems="center" spacing={2} sx={{ pb: 2 }}>
                <Grid item>
                    <Chip
                        id="onboarding-chip"
                        label={
                            <Typography variant="h2" align="center" sx={{ fontWeight: 700 }}>
                                {t(`invoicing.onboarding`)}
                            </Typography>
                        }
                        className={
                            valuesAcc.cfoOnboard
                                ? active.cfoOnboard.isActive
                                    ? classes.selectedChipActive
                                    : active.cfoOnboard.wasActive
                                    ? classes.selectedChipWasActive
                                    : classes.selectedChip
                                : active.cfoOnboard.isActive
                                ? classes.chipProActive
                                : active.cfoOnboard.wasActive
                                ? classes.chipProWasActive
                                : classes.chip
                        }
                        onClick={() => handleClickChip(valuesAcc.cfoOnboard, "cfoOnboard", true)}
                    />
                </Grid>
                <Grid item>
                    <Chip
                        id="catch-up-chip"
                        label={
                            <Typography variant="h2" align="center" sx={{ fontWeight: 700 }}>
                                {t(`invoicing.catchUp`)}
                            </Typography>
                        }
                        className={
                            valuesAcc.cfoCatchUp
                                ? active.cfoCatchUp.isActive
                                    ? classes.selectedChipActive
                                    : active.cfoCatchUp.wasActive
                                    ? classes.selectedChipWasActive
                                    : classes.selectedChip
                                : active.cfoCatchUp.isActive
                                ? classes.chipProActive
                                : active.cfoCatchUp.wasActive
                                ? classes.chipProWasActive
                                : classes.chip
                        }
                        onClick={() => handleClickChip(valuesAcc.cfoCatchUp, "cfoCatchUp", true)}
                    />
                </Grid>
                <Grid item>
                    <Chip
                        id="recurring-chip"
                        label={
                            <Typography variant="h2" align="center" sx={{ fontWeight: 700 }}>
                                {t(`invoicing.recurring`)}
                            </Typography>
                        }
                        className={
                            valuesAcc.cfoRecu
                                ? active.cfoRecu.isActive
                                    ? classes.selectedChipActive
                                    : active.cfoRecu.wasActive
                                    ? classes.selectedChipWasActive
                                    : classes.selectedChip
                                : active.cfoRecu.isActive
                                ? classes.chipProActive
                                : active.cfoRecu.wasActive
                                ? classes.chipProWasActive
                                : classes.chip
                        }
                        onClick={() => handleClickChip(valuesAcc.cfoRecu, "cfoRecu", true)}
                    />
                </Grid>
                <Grid item>
                    <Chip
                        id="consulting-chip"
                        label={
                            <Typography variant="h2" align="center" sx={{ fontWeight: 700 }}>
                                {t(`invoicing.consulting`)}
                            </Typography>
                        }
                        className={
                            valuesAcc.cfoConsul
                                ? active.cfoConsul.isActive
                                    ? classes.selectedChipActive
                                    : active.cfoConsul.wasActive
                                    ? classes.selectedChipWasActive
                                    : classes.selectedChip
                                : active.cfoConsul.isActive
                                ? classes.chipProActive
                                : active.cfoConsul.wasActive
                                ? classes.chipProWasActive
                                : classes.chip
                        }
                        onClick={() => handleClickChip(valuesAcc.cfoConsul, "cfoConsul", true)}
                    />
                </Grid>
            </Grid>
            {valuesAcc.cfoOnboard && (
                <Onboarding
                    setTexto={setTexto}
                    setalertType={setalertType}
                    setShow={setShow}
                    companyID={companyID}
                    reloadProjects={getProjects}
                    setBackDrop={setBackDrop}
                    data={projectsData.cfoOnboard}
                    serviceID={serviceData.service_id}
                    cfoSystem={cfoSystem}
                    expenseEst={expenseEst}
                />
            )}
            {valuesAcc.cfoCatchUp && (
                <CatchUp
                    setTexto={setTexto}
                    setalertType={setalertType}
                    setShow={setShow}
                    clientMarket={clientMarket}
                    companyID={companyID}
                    expenseEst={expenseEst}
                    cfoSystem={cfoSystem}
                    reloadProjects={getProjects}
                    setBackDrop={setBackDrop}
                    data={projectsData.cfoCatchUp}
                    serviceID={serviceData.service_id}
                    handleCloseDialog={handleCloseDialog}
                    infoAdd={infoAdd}
                    checkRemind={checkRemind}
                    open={open}
                    bodyMess={bodyMess}
                    showConfirm={showConfirm}
                />
            )}
            {valuesAcc.cfoRecu && (
                <Recurring
                    setTexto={setTexto}
                    setalertType={setalertType}
                    setShow={setShow}
                    setBackDrop={setBackDrop}
                    clientMarket={clientMarket}
                    companyID={companyID}
                    expenseEst={expenseEst}
                    cfoSystem={cfoSystem}
                    data={projectsData.cfoRecu}
                    reloadProjects={getProjects}
                    serviceID={serviceData.service_id}
                    handleCloseDialog={handleCloseDialog}
                    infoAdd={infoAdd}
                    checkRemind={checkRemind}
                    open={open}
                    bodyMess={bodyMess}
                    showConfirm={showConfirm}
                />
            )}
            {valuesAcc.cfoConsul && (
                <Consulting
                    setTexto={setTexto}
                    setalertType={setalertType}
                    setShow={setShow}
                    companyID={companyID}
                    reloadProjects={getProjects}
                    setBackDrop={setBackDrop}
                    data={projectsData.cfoConsul}
                    serviceID={serviceData.service_id}
                />
            )}
        </StyledContainer>
    );
};
